import React, { useState } from "react";
import { Layout, Dropdown, Grid } from "antd";
import {
  LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, CloseOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  HiOutlineDocumentArrowDown,
  HiOutlineDocumentDuplicate,
  HiOutlineHome,
  HiOutlineQueueList,
  HiOutlineQrCode,
  HiOutlineShieldCheck,
  HiOutlineSquares2X2,
  HiOutlineTicket,
} from "react-icons/hi2";
import "./Dashboard.css";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const BASE_NAV_GROUPS = [
  {
    key: "overview",
    items: [
      {
        key: "home",
        label: "Overview",
        icon: HiOutlineHome,
        path: "/dashboard/home",
      },
    ],
  },
  {
    key: "tools",
    label: "Tools",
    collapsible: false,
    items: [
      { key: "pdf-tools", label: "PDF Tools",   icon: HiOutlineSquares2X2, path: "/dashboard/pdf-tools", color: "#e5322d" },
      { key: "qr-link",   label: "Generate QR", icon: HiOutlineQueueList,  path: "/dashboard/qr-link",    color: "#0891b2" },
    ],
  },
  {
    key: "files",
    label: "My Files",
    collapsible: false,
    items: [
      { key: "pdf-list", label: "Converted PDFs", icon: HiOutlineDocumentArrowDown, path: "/dashboard/pdf-list", color: "#e5322d" },
      { key: "qr-list",  label: "My QR Codes",    icon: HiOutlineQueueList,         path: "/dashboard/qr-list",  color: "#6366f1" },
    ],
  },
];

const BUSINESS_NAV_GROUP = {
  key: "business",
  label: "Business",
  collapsible: false,
  items: [
    { key: "coupons", label: "Coupons", icon: HiOutlineTicket, path: "/dashboard/business/coupons", color: "#d97706" },
    { key: "dynamic-qr", label: "Dynamic QR Code", icon: HiOutlineQrCode, path: "/dashboard/business/dynamic-qr", color: "#7b3fe4" },
    { key: "access", label: "Access", icon: HiOutlineShieldCheck, path: "/dashboard/business/access", color: "#16a34a" },
    { key: "pages", label: "API's", icon: HiOutlineDocumentDuplicate, path: "/dashboard/business/pages", color: "#0891b2" },
  ],
};

const getCustomerType = (user = {}) =>
  String(
    user.customerType ||
    user.CustomerType ||
    user.data?.customerType ||
    user.data?.CustomerType ||
    user.user?.customerType ||
    user.user?.CustomerType ||
    ""
  ).toLowerCase();

const getUserType = (user = {}) =>
  String(user.userType || user.UserType || user.data?.userType || user.data?.UserType || "").toLowerCase();

const PDF_TOOL_PATHS = [
  "/dashboard/pdf-tools",
  "/dashboard/word2pdf",
  "/dashboard/excel2pdf",
  "/dashboard/ppt2pdf",
  "/dashboard/image2pdf",
  "/dashboard/merge",
  "/dashboard/split",
  "/dashboard/rearrange",
  "/dashboard/compress",
  "/dashboard/ocr",
  "/dashboard/esign",
  "/dashboard/addpass",
  "/dashboard/removepass",
];
const QR_CREATE_PATHS = [
  "/dashboard/qr-link",
  "/dashboard/qr-text",
  "/dashboard/qr-location",
  "/dashboard/qr-brand",
  "/dashboard/qr-social",
  "/dashboard/qr-file",
  "/dashboard/qr-vcard",
  "/dashboard/qr-product",
  "/dashboard/qr-message",
];
const BUSINESS_PATHS = [
  "/dashboard/business/coupons",
  "/dashboard/business/coupons/new",
];

const Dashboard = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const isBusinessCustomer = getCustomerType(user) === "business";
  const isStaffUser = getUserType(user) === "staff";
  const staffBusinessGroup = {
    ...BUSINESS_NAV_GROUP,
    items: BUSINESS_NAV_GROUP.items.filter((item) => item.key === "dynamic-qr"),
  };
  const navGroups = isBusinessCustomer
    ? [...BASE_NAV_GROUPS.slice(0, 2), BUSINESS_NAV_GROUP, ...BASE_NAV_GROUPS.slice(2)]
    : isStaffUser
    ? [staffBusinessGroup]
    : BASE_NAV_GROUPS;
  const allNav = navGroups.flatMap((g) => g.items);
  const screens    = useBreakpoint();
  const isMobile   = !screens.md;

  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  // Track which collapsible groups are open
  const [openGroups,  setOpenGroups]  = useState({ files: true });

  const activeKey = PDF_TOOL_PATHS.includes(location.pathname)
    ? "pdf-tools"
    : QR_CREATE_PATHS.includes(location.pathname)
    ? "qr-link"
    : BUSINESS_PATHS.includes(location.pathname)
    ? "coupons"
    : allNav.find((n) => location.pathname === n.path)?.key ?? "home";
  const activeLabel = allNav.find((n) => n.key === activeKey)?.label ?? "Overview";

  const handleLogout = () => { localStorage.clear(); navigate("/"); };

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const go = (path) => { navigate(path); if (isMobile) setMobileOpen(false); };

  const userInitial = (user.name || "U").charAt(0).toUpperCase();

  const userMenuItems = [
    {
      key: "profile",
      label: (
        <div style={{ padding: "4px 0" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>{user.name || "User"}</div>
          <div style={{ fontSize: 12, color: "#9ca3af" }}>{user.username || user.email || ""}</div>
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined style={{ color: "#e5322d" }} />,
      label: <span style={{ color: "#e5322d", fontWeight: 600 }}>Sign out</span>,
      onClick: handleLogout,
    },
  ];

  const renderNavIcon = (Icon) => <Icon className="db-nav-icon" aria-hidden="true" />;

  // ── Sidebar content ──────────────────────────────────────────────
  const siderContent = (
    <div className="db-sider-content">

      {/* Logo */}
      <div className="db-logo" onClick={() => navigate("/")}>
        <img src={require("../../assets/logo.png")} alt="Bonoxi" className="db-logo-img" />
        {!collapsed && <span className="db-logo-pill">My Account</span>}
      </div>

      {/* Nav */}
      <nav className="db-nav">
        {navGroups.map((group) => (
          <div key={group.key} className="db-nav-group-wrap">

            {/* Overview — no header */}
            {group.key === "overview" && group.items.map((item) => (
              <button
                key={item.key}
                className={`db-nav-item ${activeKey === item.key ? "active" : ""}`}
                onClick={() => go(item.path)}
                title={collapsed ? item.label : ""}
              >
                <span className="db-nav-icon-wrap">{renderNavIcon(item.icon)}</span>
                {!collapsed && <span className="db-nav-label">{item.label}</span>}
              </button>
            ))}

            {/* Groups with header */}
            {group.key !== "overview" && (
              <>
                {!collapsed && (
                  <button
                    className={`db-group-header ${group.collapsible ? "clickable" : ""}`}
                    onClick={() => group.collapsible && toggleGroup(group.key)}
                  >
                    <span className="db-group-label">{group.label}</span>
                    {group.collapsible && (
                      <span className={`db-group-chevron ${openGroups[group.key] ? "open" : ""}`}>
                        ›
                      </span>
                    )}
                  </button>
                )}
                {collapsed && <div className="db-nav-divider" />}

                {(collapsed || !group.collapsible || openGroups[group.key]) && (
                  <div className={`db-group-items ${!collapsed && group.collapsible && !openGroups[group.key] ? "hidden" : ""}`}>
                    {group.items.map((item) => (
                      <button
                        key={item.key}
                        className={`db-nav-item ${activeKey === item.key ? "active" : ""}`}
                        onClick={() => go(item.path)}
                        title={collapsed ? item.label : ""}
                        style={{ "--item-color": item.color || "#e5322d" }}
                      >
                        <span className="db-nav-icon-wrap">{renderNavIcon(item.icon)}</span>
                        {!collapsed && <span className="db-nav-label">{item.label}</span>}
                        {!collapsed && activeKey === item.key && (
                          <span className="db-nav-active-dot" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </nav>

      {/* User card */}
      <div className={`db-user-card ${collapsed ? "collapsed" : ""}`}>
        {collapsed ? (
          <div className="db-user-avatar-only" onClick={handleLogout} title="Sign out">
            <div className="db-avatar">{userInitial}</div>
          </div>
        ) : (
          <>
            <div className="db-avatar">{userInitial}</div>
            <div className="db-user-info">
              <div className="db-user-name">{user.name || "User"}</div>
              <div className="db-user-plan">Free Plan</div>
            </div>
            <button className="db-logout-btn" onClick={handleLogout} title="Sign out">
              <LogoutOutlined />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Layout className="db-root">

      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          collapsedWidth={64}
          width={256}
          theme="light"
          className="db-sider"
        >
          {siderContent}
        </Sider>
      )}

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div className="db-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <div className={`db-mobile-drawer ${mobileOpen ? "open" : ""}`}>
          <button className="db-drawer-close" onClick={() => setMobileOpen(false)}>
            <CloseOutlined />
          </button>
          {siderContent}
        </div>
      )}

      <Layout>
        {/* Top header */}
        <Header className="db-header">
          <div className="db-header-left">
            <button
              className="db-toggle"
              onClick={() => isMobile ? setMobileOpen(!mobileOpen) : setCollapsed(!collapsed)}
            >
              {isMobile
                ? <MenuUnfoldOutlined />
                : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
              }
            </button>
            <div className="db-breadcrumb">
              <span className="db-breadcrumb-parent" onClick={() => navigate("/dashboard/home")}>Dashboard</span>
              {activeKey !== "home" && (
                <>
                  <span className="db-breadcrumb-sep">›</span>
                  <span className="db-breadcrumb-current">{activeLabel}</span>
                </>
              )}
            </div>
          </div>

          <div className="db-header-right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={["click"]}>
              <div className="db-header-user">
                <div className="db-avatar sm">{userInitial}</div>
                {!isMobile && (
                  <div className="db-header-user-info">
                    <span className="db-header-name">{user.name || "User"}</span>
                    <span className="db-header-plan">Free Plan</span>
                  </div>
                )}
                <span className="db-header-chevron">›</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="db-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
